import { supabase } from './supabase';
import { BaseService } from './base-service';
import { Account, AccountService } from './profile-service';
import { Product } from './product-service';

/**
 * Interface representing a supplier profile
 */
export interface SupplierProfile {
  id: string;
  business_name?: string;
  logo_url?: string;
  description?: string;
  website?: string;
  gst_number?: string;
  pan_number?: string;
  rating?: number;
  verification_status?: string;
  joined_date?: string;
  created_at?: string;
  updated_at?: string;
  slug?: string;
  address?: string;
}

/**
 * Interface representing a supplier inquiry
 */
export interface SupplierInquiry {
  id: string;
  product_id: string;
  buyer_id: string;
  supplier_id: string;
  message: string;
  response?: string;
  status: 'pending' | 'responded' | 'closed';
  created_at?: string;
  updated_at?: string;
  // Joined fields
  product_name?: string;
  buyer_name?: string;
}

/**
 * Interface representing a supplier order
 */
export interface SupplierOrder {
  id: string;
  order_number: string;
  product_id: string;
  buyer_id: string;
  supplier_id: string;
  quantity: number;
  total_amount: number;
  currency: string;
  payment_method: string;
  payment_status: 'pending' | 'processing' | 'completed' | 'failed';
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  estimated_delivery?: string;
  delivery_date?: string;
  delivery_address?: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  notes?: string;
  internal_notes?: string;
  created_at?: string;
  updated_at?: string;
  // Joined fields
  product_name?: string;
  product_image?: string;
  buyer_name?: string;
  buyer_company?: string;
  buyer_avatar?: string;
}

/**
 * Interface representing a supplier activity
 */
export interface SupplierActivity {
  id: string;
  supplier_id: string;
  type: 'inquiry' | 'response' | 'order' | 'shipping' | 'product';
  title: string;
  description: string;
  related_id?: string;
  created_at?: string;
}

/**
 * Interface representing supplier statistics
 */
export interface SupplierStats {
  total_products: number;
  active_products: number;
  draft_products: number;
  total_inquiries: number;
  pending_inquiries: number;
  responded_inquiries: number;
  total_orders: number;
  pending_orders: number;
  processing_orders: number;
  shipped_orders: number;
  delivered_orders: number;
  total_revenue: number;
  monthly_revenue: number;
  average_rating: number;
  product_views: number;
  conversion_rate: number;
  top_performing_products: Array<{
    id: string;
    name: string;
    sales: number;
    revenue: number;
  }>;
  monthly_stats: Array<{
    month: string;
    sales: number;
    revenue: number;
    inquiries: number;
    views: number;
  }>;
}

/**
 * Service for managing supplier data
 */
export class SupplierService extends BaseService {
  constructor() {
    super(supabase);
  }
  
  /**
   * Get supplier profile details from supplier_profiles table
   * @param supplierId The supplier ID
   * @returns The supplier profile or null if not found
   */
  async getSupplierProfileDetails(supplierId: string): Promise<SupplierProfile | null> {
    try {
      const { data, error } = await this.supabase
        .from('supplier_profiles')
        .select('*')
        .eq('id', supplierId);
        
      if (error) {
        this.handleError(error, 'fetching supplier profile details');
        return null;
      }

      // Handle case where no supplier profile exists
      if (!data || data.length === 0) {
        return null;
      }

      return data[0];
    } catch (error) {
      this.handleError(error, 'fetching supplier profile details');
      return null;
    }
  }
  
  /**
   * Get complete supplier information (combining profile and supplier_profile)
   * @param supplierId The supplier ID
   * @returns Combined supplier information or null if not found
   */
  async getCompleteSupplierInfo(supplierId: string): Promise<any> {
    try {
      // Get basic profile
      const { data: profileArray, error: profileError } = await this.supabase
        .from('accounts')
        .select('id, full_name, phone, email, city, state, country, user_type, company_name, avatar_url, is_verified')
        .eq('id', supplierId);
      
      if (profileError) {
        this.handleError(profileError, 'fetching complete supplier info - accounts');
        return null;
      }

      // Handle case where no account exists
      if (!profileArray || profileArray.length === 0) {
        console.log(`No account found for supplier ID: ${supplierId}`);
        return null;
      }

      const profileData = profileArray[0];
      
      // Get supplier profile using the renamed method
      const supplierData = await this.getSupplierProfileDetails(supplierId);
      
      // It's okay if supplier profile doesn't exist
      
      // Combine the data
      return {
        ...profileData,
        supplier_profile: supplierData || {}
      };
    } catch (error) {
      this.handleError(error, 'fetching complete supplier info');
      return null;
    }
  }

  /**
   * Get supplier basic profile from accounts table
   * This is used for basic supplier information display
   * @param supplierId The supplier's user ID
   * @returns Basic supplier profile information
   */
  async getSupplierBasicProfile(supplierId: string) {
    try {
      const { data, error } = await this.supabase
        .from('accounts')
        .select('full_name, phone, email, city, state, country, user_type')
        .eq('id', supplierId);
        
      if (error) {
        this.handleError(error, 'fetching supplier basic profile');
        return null;
      }

      // Handle case where no account exists
      if (!data || data.length === 0) {
        return null;
      }

      return data[0];
    } catch (error) {
      this.handleError(error, 'fetching supplier basic profile');
      return null;
    }
  }

  /**
   * Get supplier products
   * @param supplierId The supplier ID
   * @returns Array of products or empty array if none found
   */
  async getSupplierProducts(supplierId: string): Promise<Product[]> {
    try {
      const { data, error } = await this.supabase
        .from('products')
        .select(`
          *,
          product_images (
            image_url,
            is_primary,
            display_order
          ),
          categories (
            name
          )
        `)
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Format the products to include image URLs and category names
      const formattedProducts = (data || []).map(product => {
        // Extract primary image or first image
        const productImages = product.product_images || [];
        const primaryImage = productImages.find((img: any) => img.is_primary);
        const firstImage = productImages.length > 0 ? productImages[0] : null;
        const mainImage = primaryImage || firstImage;
        
        // Extract all image URLs
        const images = productImages
          .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0))
          .map((img: any) => img.image_url);
        
        // Extract category name
        const categoryName = product.categories?.name || 'Uncategorized';
        
        // Remove the nested objects and add formatted data
        const { product_images, categories, ...productData } = product;
        
        return {
          ...productData,
          images,
          main_image: mainImage?.image_url || null,
          category_name: categoryName
        };
      });
      
      return formattedProducts;
    } catch (error) {
      this.handleError(error, 'fetching supplier products');
      return [];
    }
  }

  /**
   * Get supplier inquiries
   * @param supplierId The supplier ID
   * @returns Array of inquiries or empty array if none found
   */
  async getSupplierInquiries(supplierId: string): Promise<SupplierInquiry[]> {
    try {
      // First get the basic inquiry data
      const { data: inquiryData, error: inquiryError } = await this.supabase
        .from('inquiries')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });

      if (inquiryError) throw inquiryError;
      if (!inquiryData || inquiryData.length === 0) return [];
      
      // Format the inquiries with placeholder data
      const formattedInquiries = inquiryData.map(inquiry => {
        return {
          ...inquiry,
          product_name: 'Product ' + inquiry.product_id,
          buyer_name: 'Buyer ' + inquiry.buyer_id
        };
      });

      return formattedInquiries;
    } catch (error) {
      this.handleError(error, 'fetching supplier inquiries');
      return [];
    }
  }

  /**
   * Get supplier orders
   * @param supplierId The supplier ID
   * @returns Array of orders or empty array if none found
   */
  async getSupplierOrders(supplierId: string): Promise<SupplierOrder[]> {
    try {
      // First get the basic order data
      const { data: orderData, error: orderError } = await this.supabase
        .from('orders')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });

      if (orderError) throw orderError;
      if (!orderData || orderData.length === 0) return [];
      
      // Format the orders with placeholder data
      const formattedOrders = orderData.map(order => {
        return {
          ...order,
          product_name: 'Product ' + order.product_id,
          product_image: null,
          buyer_name: 'Buyer ' + order.buyer_id,
          buyer_company: '',
          buyer_avatar: ''
        };
      });

      return formattedOrders;
    } catch (error) {
      this.handleError(error, 'fetching supplier orders');
      return [];
    }
  }

  /**
   * Get supplier recent activities
   * @param supplierId The supplier ID
   * @param limit The maximum number of activities to return (default: 10)
   * @returns Array of activities or empty array if none found
   */
  async getSupplierActivities(supplierId: string, limit: number = 10): Promise<SupplierActivity[]> {
    try {
      // Since the supplier_activities table doesn't exist, we'll generate mock activities
      // from inquiries and orders
      
      // Get recent inquiries
      const { data: inquiryData, error: inquiryError } = await this.supabase
        .from('inquiries')
        .select('id, title, created_at, status')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (inquiryError) throw inquiryError;
      
      // Generate activities from inquiries
      const activities: SupplierActivity[] = (inquiryData || []).map(inquiry => ({
        id: `inq-${inquiry.id}`,
        supplier_id: supplierId,
        type: 'inquiry', // Using valid type from SupplierActivity enum
        activity_type: 'inquiry_received',
        title: 'New Inquiry Received',
        description: `You received a new inquiry: ${inquiry.title}`,
        created_at: inquiry.created_at,
        related_id: inquiry.id,
        related_type: 'inquiry',
        status: inquiry.status
      }));
      
      return activities;
    } catch (error) {
      this.handleError(error, 'fetching supplier activities');
      return [];
    }
  }

  /**
   * Get supplier statistics
   * @param supplierId The supplier ID
   * @returns Supplier statistics or null if not found
   */
  async getSupplierStats(supplierId: string): Promise<SupplierStats | null> {
    try {
      // Get basic product data - handle case where status column might not exist
      const { data: products, error: productError } = await this.supabase
        .from('products')
        .select('id, views_count, rating')
        .eq('supplier_id', supplierId);

      if (productError) throw productError;
      
      // Get basic inquiry data
      const { data: inquiries, error: inquiryError } = await this.supabase
        .from('inquiries')
        .select('id, status')
        .eq('supplier_id', supplierId);

      if (inquiryError) throw inquiryError;
      
      // Get basic order data
      const { data: orders, error: orderError } = await this.supabase
        .from('orders')
        .select('id, status, total_amount, created_at')
        .eq('supplier_id', supplierId);

      if (orderError) throw orderError;
      
      // Count products - handle case where status column might not exist
      const totalProducts = products?.length || 0;
      
      // Use optional chaining and type checking to avoid errors if status doesn't exist
      const activeProducts = products?.filter(p => (p as any).status === 'active').length || 0;
      const draftProducts = products?.filter(p => (p as any).status === 'draft').length || 0;
      
      // If we get an error about missing status column, we'll still have totalProducts
      // but activeProducts and draftProducts will be 0
      
      // Count inquiries by status
      const pendingInquiries = inquiries?.filter(i => i.status === 'pending').length || 0;
      const respondedInquiries = inquiries?.filter(i => i.status === 'responded').length || 0;
      const totalInquiries = inquiries?.length || 0;
      
      // Count orders by status
      const pendingOrders = orders?.filter(o => o.status === 'pending').length || 0;
      const processingOrders = orders?.filter(o => o.status === 'processing').length || 0;
      const shippedOrders = orders?.filter(o => o.status === 'shipped').length || 0;
      const deliveredOrders = orders?.filter(o => o.status === 'delivered').length || 0;
      const totalOrders = orders?.length || 0;
      
      // Calculate revenue
      const deliveredOrdersData = orders?.filter(o => o.status === 'delivered') || [];
      const totalRevenue = deliveredOrdersData.reduce((sum: number, order: any) => sum + (order.total_amount || 0), 0);
      
      // Calculate monthly revenue (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const monthlyRevenue = deliveredOrdersData.reduce((sum: number, order: any) => {
        const orderDate = new Date(order.created_at || '');
        return orderDate >= thirtyDaysAgo ? sum + (order.total_amount || 0) : sum;
      }, 0);
      
      // Calculate product views and ratings
      const productViews = products?.reduce((sum: number, product: any) => sum + (product.views_count || 0), 0) || 0;
      const ratingsData = products?.filter(p => p.rating !== null && p.rating !== undefined) || [];
      const averageRating = ratingsData.length > 0
        ? ratingsData.reduce((sum: number, product: any) => sum + (product.rating || 0), 0) / ratingsData.length
        : 0;
      
      // Calculate conversion rate
      const conversionRate = totalInquiries > 0 ? (totalOrders / totalInquiries) * 100 : 0;
      
      // Create mock data for top products and monthly stats
      const topProducts = products?.slice(0, 3).map((p: any) => ({
        id: p.id,
        name: `Product ${p.id}`,
        sales: Math.floor(Math.random() * 100), // Changed from sales_count to sales to match interface
        revenue: Math.floor(Math.random() * 10000)
      })) || [];
      
      const monthlyStats = [
        { month: 'Jan', inquiries: 12, sales: 5, revenue: 25000, views: 120 },
        { month: 'Feb', inquiries: 18, sales: 8, revenue: 40000, views: 180 },
        { month: 'Mar', inquiries: 15, sales: 6, revenue: 30000, views: 150 },
        { month: 'Apr', inquiries: 22, sales: 10, revenue: 50000, views: 220 },
        { month: 'May', inquiries: 20, sales: 9, revenue: 45000, views: 200 },
        { month: 'Jun', inquiries: 25, sales: 12, revenue: 60000, views: 250 }
      ];
      
      // Format the statistics
      const stats: SupplierStats = {
        total_products: totalProducts,
        active_products: activeProducts,
        draft_products: draftProducts,
        total_inquiries: totalInquiries,
        pending_inquiries: pendingInquiries,
        responded_inquiries: respondedInquiries,
        total_orders: totalOrders,
        pending_orders: pendingOrders,
        processing_orders: processingOrders,
        shipped_orders: shippedOrders,
        delivered_orders: deliveredOrders,
        total_revenue: totalRevenue,
        monthly_revenue: monthlyRevenue,
        average_rating: parseFloat(averageRating.toFixed(1)),
        product_views: productViews,
        conversion_rate: parseFloat(conversionRate.toFixed(1)),
        top_performing_products: topProducts,
        monthly_stats: monthlyStats
      };

      return stats;
    } catch (error) {
      this.handleError(error, 'fetching supplier statistics');
      return null;
    }
  }

  /**
   * Respond to an inquiry
   * @param inquiryId The inquiry ID
   * @param response The response message
   * @returns True if update was successful, false otherwise
   */
  async respondToInquiry(inquiryId: string, response: string): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('inquiries')
        .update({
          response,
          status: 'responded',
          updated_at: new Date().toISOString()
        })
        .eq('id', inquiryId);

      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'responding to inquiry');
      return false;
    }
  }

  /**
   * Update order status
   * @param orderId The order ID
   * @param status The new status
   * @returns True if update was successful, false otherwise
   */
  async updateOrderStatus(orderId: string, status: SupplierOrder['status']): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('orders')
        .update({
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'updating order status');
      return false;
    }
  }

  /**
   * Log supplier activity
   * @param activity The activity data
   * @returns True if creation was successful, false otherwise
   */
  async logActivity(activity: Omit<SupplierActivity, 'id' | 'created_at'>): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('supplier_activities')
        .insert({
          ...activity,
          created_at: new Date().toISOString()
        });

      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'logging supplier activity');
      return false;
    }
  }
}
